import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { accountsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PeriodToolbar, { FilingActionsNotice } from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES, formatMonth } from '../lib/period';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Receipt, TrendingUp, TrendingDown, Calculator, Lock, Unlock,
  Settings2, Clock, Shield, Check, Pencil, X, Save, Plus, ChevronRight,
} from 'lucide-react';

const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const TABS = [
  { id: 'definitions', label: 'Definitions', icon: Settings2 },
  { id: 'applied', label: 'Applied', icon: Shield },
  { id: 'history', label: 'History', icon: Clock },
];

const ACCOUNTS = ['FOOD', 'BEVERAGE', 'LIQUOR'];
const TAX_TYPES = ['GST', 'VAT', 'NONE', 'CUSTOM'];
const TAX_MODES = ['EXCLUSIVE', 'INCLUSIVE'];

const acctColors = {
  FOOD: { badge: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10', bar: 'bg-emerald-500', dot: 'bg-emerald-400' },
  BEVERAGE: { badge: 'text-blue-400 border-blue-500/30 bg-blue-500/10', bar: 'bg-blue-500', dot: 'bg-blue-400' },
  LIQUOR: { badge: 'text-violet-400 border-violet-500/30 bg-violet-500/10', bar: 'bg-violet-500', dot: 'bg-violet-400' },
};

const sourceLabels = { OUTLET_TAX_CONFIG: 'Outlet Override', ORG_TAX_CONFIG: 'Org Default', UNKNOWN: 'Not Configured' };
const sourceColors = {
  OUTLET_TAX_CONFIG: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/25',
  ORG_TAX_CONFIG: 'text-teal-400 bg-teal-500/10 border-teal-500/25',
  UNKNOWN: 'text-red-400 bg-red-500/10 border-red-500/25',
};

// ─── Tax Definitions Tab ─────────────────────────────────────

function TaxDefinitionsTab() {
  const [resolved, setResolved] = useState({});
  const [orgConfig, setOrgConfig] = useState(null);
  const [outletConfig, setOutletConfig] = useState(null);
  const [hasOutletOverride, setHasOutletOverride] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'org' | 'outlet'
  const [editRules, setEditRules] = useState({});

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await accountsAPI.getResolvedTaxConfig();
      const d = res.data;
      setResolved(d.resolved || {});
      setOrgConfig(d.org_default_config);
      setOutletConfig(d.outlet_config);
      setHasOutletOverride(d.has_outlet_override);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load tax config');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const startEdit = (scope) => {
    const config = scope === 'outlet' ? outletConfig : orgConfig;
    const rules = config?.rules || {};
    const editableRules = {};
    for (const acct of ACCOUNTS) {
      const r = rules[acct] || {};
      editableRules[acct] = {
        tax_type: r.tax_type || 'GST',
        tax_mode: r.tax_mode || 'EXCLUSIVE',
        components: (r.components || []).map(c => ({ name: c.name, rate: c.rate })),
        total_rate: r.total_rate || 0,
      };
    }
    setEditRules(editableRules);
    setEditing(scope);
  };

  const updateComponent = (acct, idx, field, value) => {
    setEditRules(prev => {
      const next = { ...prev };
      const comps = [...next[acct].components];
      comps[idx] = { ...comps[idx], [field]: field === 'rate' ? parseFloat(value) || 0 : value };
      next[acct] = { ...next[acct], components: comps, total_rate: comps.reduce((s, c) => s + (c.rate || 0), 0) };
      return next;
    });
  };

  const addComponent = (acct) => {
    setEditRules(prev => {
      const next = { ...prev };
      next[acct] = { ...next[acct], components: [...next[acct].components, { name: 'CGST', rate: 0 }] };
      return next;
    });
  };

  const removeComponent = (acct, idx) => {
    setEditRules(prev => {
      const next = { ...prev };
      const comps = next[acct].components.filter((_, i) => i !== idx);
      next[acct] = { ...next[acct], components: comps, total_rate: comps.reduce((s, c) => s + (c.rate || 0), 0) };
      return next;
    });
  };

  const saveConfig = async () => {
    try {
      const payload = { rules: editRules };
      if (editing === 'outlet') {
        await accountsAPI.createOutletTaxOverride(payload);
        toast.success('Outlet tax override saved');
      } else {
        await accountsAPI.createOrgDefaultTaxConfig(payload);
        toast.success('Org default tax config saved');
      }
      setEditing(null);
      fetchConfig();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.validation_errors) {
        toast.error(detail.validation_errors.join(', '));
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to save config');
      }
    }
  };

  if (loading) return <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={`def-skel-${i}`} className="h-24 rounded-xl" />)}</div>;

  return (
    <div className="space-y-5">
      {/* Active Tax Regime */}
      <Card className="bg-zinc-900/60 border-zinc-800" data-testid="active-tax-regime">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base text-zinc-200">Active Tax Regime</CardTitle>
              <p className="text-xs text-zinc-500 mt-0.5">
                {hasOutletOverride ? 'Outlet-level override active' : 'Inheriting from org default'}
              </p>
            </div>
            <Badge variant="outline" className={hasOutletOverride ? sourceColors.OUTLET_TAX_CONFIG : sourceColors.ORG_TAX_CONFIG}>
              {hasOutletOverride ? 'Outlet Override' : 'Org Default'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {ACCOUNTS.map(acct => {
              const r = resolved[acct] || {};
              const compStr = (r.components || []).map(c => `${c.name} ${c.rate}%`).join(' + ');
              return (
                <div key={acct} className="bg-zinc-800/40 rounded-lg p-3 space-y-1.5" data-testid={`regime-${acct.toLowerCase()}`}>
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className={acctColors[acct]?.badge}>{acct}</Badge>
                    <span className="text-xs text-zinc-400 font-mono">{r.total_rate || 0}%</span>
                  </div>
                  <p className="text-xs text-zinc-300">{r.tax_type || '—'} / {r.tax_mode || '—'}</p>
                  <p className="text-[10px] text-zinc-500">{compStr || 'No components'}</p>
                  <div className="flex items-center gap-1 pt-1">
                    <div className={`w-1.5 h-1.5 rounded-full ${SOURCE_DOT_CLASS[r.source] || 'bg-red-400'}`} />
                    <span className="text-[10px] text-zinc-500">{sourceLabels[r.source] || r.source}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Edit / Create Section */}
      {editing ? (
        <Card className="bg-zinc-900/60 border-amber-500/30" data-testid="tax-config-editor">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-amber-300">
                {editing === 'outlet' ? 'Edit Outlet Override' : 'Edit Org Default'}
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setEditing(null)} className="text-zinc-400 text-xs">
                  <X className="w-3 h-3 mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={saveConfig} className="bg-amber-500 hover:bg-amber-600 text-black text-xs" data-testid="save-tax-config-btn">
                  <Save className="w-3 h-3 mr-1" /> Save
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {ACCOUNTS.map(acct => (
              <div key={acct} className="bg-zinc-800/40 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className={acctColors[acct]?.badge}>{acct}</Badge>
                  <Select value={editRules[acct]?.tax_type || 'GST'} onValueChange={v => setEditRules(p => ({ ...p, [acct]: { ...p[acct], tax_type: v } }))}>
                    <SelectTrigger className="w-24 h-7 bg-zinc-800 border-zinc-700 text-xs" data-testid={`edit-${acct.toLowerCase()}-type`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {TAX_TYPES.map(t => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={editRules[acct]?.tax_mode || 'EXCLUSIVE'} onValueChange={v => setEditRules(p => ({ ...p, [acct]: { ...p[acct], tax_mode: v } }))}>
                    <SelectTrigger className="w-28 h-7 bg-zinc-800 border-zinc-700 text-xs" data-testid={`edit-${acct.toLowerCase()}-mode`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {TAX_MODES.map(m => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-zinc-400 font-mono ml-auto">Total: {editRules[acct]?.total_rate || 0}%</span>
                </div>
                <div className="space-y-1 pl-4">
                  {(editRules[acct]?.components || []).map((comp, idx) => (
                    <div key={`${acct}-comp-${idx}`} className="flex items-center gap-2">
                      <Select value={comp.name} onValueChange={v => updateComponent(acct, idx, 'name', v)}>
                        <SelectTrigger className="w-32 h-7 bg-zinc-800 border-zinc-700 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {['CGST', 'SGST', 'IGST', 'VAT', 'CESS', 'SERVICE_CHARGE'].map(n => (
                            <SelectItem key={n} value={n} className="text-xs">{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number" step="0.5" min="0"
                        value={comp.rate}
                        onChange={e => updateComponent(acct, idx, 'rate', e.target.value)}
                        className="w-20 h-7 bg-zinc-800 border-zinc-700 text-xs text-right"
                      />
                      <span className="text-xs text-zinc-500">%</span>
                      <button onClick={() => removeComponent(acct, idx)} className="text-red-400 hover:text-red-300 p-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => addComponent(acct)} className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1 pt-1">
                    <Plus className="w-3 h-3" /> Add component
                  </button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => startEdit('org')} className="text-xs border-zinc-700" data-testid="edit-org-default-btn">
            <Pencil className="w-3 h-3 mr-1" /> Edit Org Default
          </Button>
          <Button variant="outline" size="sm" onClick={() => startEdit('outlet')} className="text-xs border-zinc-700" data-testid="edit-outlet-override-btn">
            <Pencil className="w-3 h-3 mr-1" /> {hasOutletOverride ? 'Edit Outlet Override' : 'Create Outlet Override'}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Tax Applied Tab (existing dashboard content) ────────────

const TaxCard = ({ title, value, icon: Icon, variant = 'default', subtitle }) => {
  const variants = {
    default: 'border-border/50', input: 'border-emerald-500/30 bg-emerald-500/5',
    output: 'border-blue-500/30 bg-blue-500/5', liability: 'border-amber-500/30 bg-amber-500/5',
    tds: 'border-purple-500/30 bg-purple-500/5', vat: 'border-rose-500/30 bg-rose-500/5',
    vatInput: 'border-pink-500/30 bg-pink-500/5', vatOutput: 'border-orange-500/30 bg-orange-500/5',
  };
  const iconColors = {
    default: 'text-muted-foreground', input: 'text-emerald-400', output: 'text-blue-400',
    liability: 'text-amber-400', tds: 'text-purple-400', vat: 'text-rose-400',
    vatInput: 'text-pink-400', vatOutput: 'text-orange-400',
  };
  return (
    <Card className={`bg-card/50 backdrop-blur-sm ${variants[variant]}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-mono font-semibold tracking-tight tabular-nums">{formatCurrency(value)}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl bg-background/50 ${iconColors[variant]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Month-by-month composition + Total Selected Period (totals = server sum of the monthly rows).
const MONTHLY_COLS = [
  ['total_output_gst', 'Output GST'], ['total_input_gst', 'Input GST'], ['net_gst_liability', 'Net GST'],
  ['output_vat', 'Output VAT'], ['input_vat', 'Input VAT'], ['net_vat_liability', 'Net VAT'], ['net_tax_liability', 'Net Tax'],
];

function MonthlyTaxTable({ summary, tds, onPickMonth }) {
  const tdsBy = Object.fromEntries((tds?.monthly || []).map((r) => [r.tax_period, r]));
  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border/50" data-testid="tax-monthly-table">
      <CardHeader><CardTitle className="text-lg font-manrope">Month-by-month · {summary.months.length} months</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Month</TableHead>
              {MONTHLY_COLS.map(([, l]) => <TableHead key={l} className="text-xs text-right">{l}</TableHead>)}
              <TableHead className="text-xs text-right">TDS Payable</TableHead>
              <TableHead className="text-xs">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.monthly.map((r) => (
              <TableRow key={r.tax_period} className="cursor-pointer hover:bg-muted/30" onClick={() => onPickMonth(r.tax_period)} data-testid={`tax-month-row-${r.tax_period}`}>
                <TableCell className="font-medium">{formatMonth(r.tax_period)}</TableCell>
                {MONTHLY_COLS.map(([k]) => <TableCell key={k} className={`text-right font-mono ${r[k] < 0 ? 'text-emerald-400' : ''}`}>{formatCurrency(r[k])}</TableCell>)}
                <TableCell className="text-right font-mono">{formatCurrency(tdsBy[r.tax_period]?.tds_payable)}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{r.status || 'OPEN'}{r.input_tax_state === 'PARTIAL' ? ' · ITC partial' : ''}</Badge></TableCell>
              </TableRow>
            ))}
            <TableRow className="border-t-2 border-border/80 font-semibold" data-testid="tax-month-total-row">
              <TableCell>Total Selected Period</TableCell>
              {MONTHLY_COLS.map(([k]) => <TableCell key={k} className="text-right font-mono" data-testid={`tax-total-${k}`}>{formatCurrency(summary.totals?.[k])}</TableCell>)}
              <TableCell className="text-right font-mono">{formatCurrency(tds?.totals?.tds_payable)}</TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
        <p className="text-[11px] text-muted-foreground mt-2">Negative net = credit in that month, shown per month (not netted away). Click a month to open it alone for filing.</p>
      </CardContent>
    </Card>
  );
}

function Gstr3bMonthTable({ gstr3b, onPickMonth }) {
  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border/50" data-testid="gstr3b-month-table">
      <CardHeader><CardTitle className="text-lg font-manrope">GSTR-3B by filing month</CardTitle></CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Month</TableHead>
              <TableHead className="text-xs text-right">Outward Taxable</TableHead>
              <TableHead className="text-xs text-right">Total ITC</TableHead>
              <TableHead className="text-xs text-right">Net Payable</TableHead>
              <TableHead className="text-xs">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {gstr3b.monthly.map((r) => (
              <TableRow key={r.tax_period} className="cursor-pointer hover:bg-muted/30" onClick={() => onPickMonth(r.tax_period)} data-testid={`gstr3b-month-row-${r.tax_period}`}>
                <TableCell className="font-medium">{formatMonth(r.tax_period)}</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(r.outward_taxable_supplies)}</TableCell>
                <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(r.total_input_tax)}</TableCell>
                <TableCell className="text-right font-mono text-amber-400">{formatCurrency(r.net_tax_payable)}</TableCell>
                <TableCell><Badge variant="outline" className={r.status === 'LOCKED' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}>{r.status}</Badge></TableCell>
              </TableRow>
            ))}
            <TableRow className="border-t-2 border-border/80 font-semibold" data-testid="gstr3b-total-row">
              <TableCell>Total Selected Period</TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(gstr3b.totals?.outward_taxable_supplies)}</TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(gstr3b.totals?.total_input_tax)}</TableCell>
              <TableCell className="text-right font-mono">{formatCurrency(gstr3b.totals?.net_tax_payable)}</TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function TaxAppliedTab({ months, onResolved, onPickMonth }) {
  const [taxSummary, setTaxSummary] = useState(null);
  const [gstr3b, setGstr3b] = useState(null);
  const [tdsSummary, setTdsSummary] = useState(null);
  const [inputTax, setInputTax] = useState(null);
  const [outputTax, setOutputTax] = useState(null);
  const [vatBreakdown, setVatBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Every call receives the same selected filing months.
      onResolved?.(null);
      const params = { months: months.join(',') };
      const [summaryRes, gstr3bRes, tdsRes, inputRes, outputRes, vatRes] = await Promise.all([
        accountsAPI.getTaxSummary(months),
        accountsAPI.getGSTR3B(months),
        accountsAPI.getTDSSummary(months),
        accountsAPI.getInputTax(params),
        accountsAPI.getOutputTax(params),
        accountsAPI.getVatBreakdown(months),
      ]);
      setTaxSummary(summaryRes.data);
      onResolved?.(summaryRes.data);
      setGstr3b(gstr3bRes.data);
      setTdsSummary(tdsRes.data);
      setInputTax(inputRes.data);
      setOutputTax(outputRes.data);
      setVatBreakdown(vatRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load tax data');
    }
    setLoading(false);
  }, [months.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);
  const multi = months.length > 1;

  if (loading) return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={`app-skel-${i}`} className="h-32 rounded-xl" />)}</div>;

  return (
    <div className="space-y-6">
      <FilingActionsNotice months={months} />
      {multi && taxSummary?.monthly && <MonthlyTaxTable summary={taxSummary} tds={tdsSummary} onPickMonth={onPickMonth} />}
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <TaxCard title="Input GST (ITC)" value={taxSummary?.total_input_gst || (inputTax?.totals?.CGST || 0) + (inputTax?.totals?.SGST || 0)} icon={TrendingDown} variant="input" subtitle="GST paid on purchases" />
        <TaxCard title="Output GST" value={taxSummary?.total_output_gst || (outputTax?.totals?.CGST || 0) + (outputTax?.totals?.SGST || 0)} icon={TrendingUp} variant="output" subtitle="GST collected on sales" />
        <TaxCard title="Net GST Liability" value={taxSummary?.net_gst_liability || (taxSummary?.total_output_gst || 0) - (taxSummary?.total_input_gst || 0)} icon={Calculator} variant="liability" subtitle="Output GST - Input GST" />
        <TaxCard title="TDS Payable" value={tdsSummary?.tds_payable || 0} icon={Receipt} variant="tds" subtitle="TDS deducted" />
      </div>

      {/* VAT Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TaxCard title="Input VAT" value={taxSummary?.input_vat || 0} icon={TrendingDown} variant="vatInput" subtitle="VAT on liquor purchases" />
        <TaxCard title="Output VAT" value={taxSummary?.output_vat || 0} icon={TrendingUp} variant="vatOutput" subtitle="VAT on liquor sales" />
        <TaxCard title="Net VAT Liability" value={taxSummary?.net_vat_liability || (taxSummary?.output_vat || 0) - (taxSummary?.input_vat || 0)} icon={Calculator} variant="vat" subtitle="Output VAT - Input VAT" />
      </div>

      {/* VAT Calculation Breakdown */}
      {vatBreakdown && (vatBreakdown.output_vat?.total_vat > 0 || vatBreakdown.input_vat?.vat_amount > 0) && (
        <Card className="bg-card/50 backdrop-blur-sm border-border/50" data-testid="vat-breakdown-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-manrope">VAT Summary — How it's calculated</CardTitle>
            <Badge variant="outline" className="bg-violet-500/10 text-violet-400 border-violet-500/25">
              {vatBreakdown.output_vat?.total_entries || 0} entries
            </Badge>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Formula strip */}
            <div className="flex items-center gap-2 flex-wrap text-sm font-mono p-3 rounded-lg bg-zinc-800/60 border border-zinc-700/40" data-testid="vat-formula">
              <span className="text-blue-400">Output VAT</span>
              <span className="text-zinc-500">=</span>
              <span className="text-zinc-300">Σ (Taxable Amount × VAT Rate)</span>
              <span className="text-zinc-500">=</span>
              <span className="text-zinc-300">{formatCurrency(vatBreakdown.output_vat?.total_taxable || 0)}</span>
              <span className="text-zinc-500">×</span>
              <span className="text-zinc-300">{vatBreakdown.output_vat?.effective_rate || 0}%</span>
              <span className="text-zinc-500">=</span>
              <span className="text-blue-400 font-bold">{formatCurrency(vatBreakdown.output_vat?.total_vat || 0)}</span>
            </div>

            {/* By Account */}
            {vatBreakdown.output_vat?.by_account?.length > 0 && (
              <div>
                <h4 className="font-medium text-muted-foreground text-sm mb-2">By Account</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Account</TableHead>
                      <TableHead className="text-xs text-right">VAT Rate</TableHead>
                      <TableHead className="text-xs text-right">Taxable Amount</TableHead>
                      <TableHead className="text-xs text-right">VAT Amount</TableHead>
                      <TableHead className="text-xs text-right">Entries</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vatBreakdown.output_vat.by_account.map((row, i) => {
                      const c = acctColors[row.account] || { badge: 'text-zinc-400 border-zinc-500/30 bg-zinc-500/10', dot: 'bg-zinc-400' };
                      return (
                        <TableRow key={i} data-testid={`vat-account-row-${row.account}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${c.dot}`} />
                              <Badge variant="outline" className={`text-[10px] ${c.badge}`}>{row.account}</Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">{row.vat_rate}%</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(row.taxable_amount)}</TableCell>
                          <TableCell className="text-right font-mono font-bold text-blue-400">{formatCurrency(row.vat_amount)}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{row.entry_count}</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow className="border-t-2 border-border/80 font-medium">
                      <TableCell colSpan={2}>Total Output VAT</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(vatBreakdown.output_vat?.total_taxable)}</TableCell>
                      <TableCell className="text-right font-mono text-blue-400 font-bold">{formatCurrency(vatBreakdown.output_vat?.total_vat)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{vatBreakdown.output_vat?.total_entries}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}

            {/* By Day */}
            {vatBreakdown.output_vat?.by_day?.length > 0 && (
              <div>
                <h4 className="font-medium text-muted-foreground text-sm mb-2">Day-wise VAT Collection</h4>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs text-right">Taxable</TableHead>
                        <TableHead className="text-xs text-right">VAT</TableHead>
                        <TableHead className="text-xs text-right">Entries</TableHead>
                        <TableHead className="text-xs text-right">% of Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vatBreakdown.output_vat.by_day.map((day, i) => {
                        const pct = vatBreakdown.output_vat.total_vat > 0 ? (day.vat_amount / vatBreakdown.output_vat.total_vat * 100).toFixed(1) : 0;
                        return (
                          <TableRow key={i} data-testid={`vat-day-row-${day.date}`}>
                            <TableCell className="font-mono text-sm">{day.date}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(day.taxable_amount)}</TableCell>
                            <TableCell className="text-right font-mono text-blue-400">{formatCurrency(day.vat_amount)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{day.entry_count}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 bg-zinc-800 rounded-full h-1.5 overflow-hidden">
                                  <div className="h-full bg-violet-500/60 rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                                </div>
                                <span className="text-xs text-muted-foreground w-10 text-right">{pct}%</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Net VAT Liability calculation */}
            <div className="p-3 rounded-lg bg-zinc-800/60 border border-zinc-700/40 space-y-1.5" data-testid="vat-net-calc">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Output VAT (collected on sales)</span>
                <span className="font-mono text-blue-400">{formatCurrency(vatBreakdown.output_vat?.total_vat)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Less: Input VAT (paid on purchases)</span>
                <span className="font-mono text-emerald-400">- {formatCurrency(vatBreakdown.input_vat?.vat_amount)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold border-t border-zinc-700/40 pt-1.5">
                <span>Net VAT Liability</span>
                <span className="font-mono text-amber-400">{formatCurrency(vatBreakdown.net_vat_liability)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* GSTR-3B */}
      {multi && gstr3b?.monthly && <Gstr3bMonthTable gstr3b={gstr3b} onPickMonth={onPickMonth} />}
      {!multi && gstr3b && (
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-manrope">GSTR-3B Summary</CardTitle>
            <Badge variant="outline" className={gstr3b.status === 'LOCKED' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}>{gstr3b.status}</Badge>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <h4 className="font-medium text-muted-foreground">3.1(a) Outward Supplies</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Taxable</span><span className="font-mono">{formatCurrency(gstr3b.outward_taxable_supplies)}</span></div>
                  <div className="flex justify-between"><span>CGST</span><span className="font-mono">{formatCurrency(gstr3b.outward_cgst)}</span></div>
                  <div className="flex justify-between"><span>SGST</span><span className="font-mono">{formatCurrency(gstr3b.outward_sgst)}</span></div>
                  <div className="flex justify-between"><span>IGST</span><span className="font-mono">{formatCurrency(gstr3b.outward_igst)}</span></div>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-medium text-muted-foreground">4 Input Tax Credit</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>CGST</span><span className="font-mono text-emerald-400">{formatCurrency(gstr3b.input_cgst)}</span></div>
                  <div className="flex justify-between"><span>SGST</span><span className="font-mono text-emerald-400">{formatCurrency(gstr3b.input_sgst)}</span></div>
                  <div className="flex justify-between"><span>IGST</span><span className="font-mono text-emerald-400">{formatCurrency(gstr3b.input_igst)}</span></div>
                  <div className="flex justify-between font-medium pt-2 border-t border-border/50"><span>Total ITC</span><span className="font-mono text-emerald-400">{formatCurrency(gstr3b.total_input_tax)}</span></div>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-medium text-muted-foreground">6.1 Net Tax Payable</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Net CGST</span><span className="font-mono">{formatCurrency(gstr3b.net_cgst_payable)}</span></div>
                  <div className="flex justify-between"><span>Net SGST</span><span className="font-mono">{formatCurrency(gstr3b.net_sgst_payable)}</span></div>
                  <div className="flex justify-between"><span>Net IGST</span><span className="font-mono">{formatCurrency(gstr3b.net_igst_payable)}</span></div>
                  <div className="flex justify-between font-medium pt-2 border-t border-border/50"><span>Total</span><span className="font-mono text-amber-400 text-lg">{formatCurrency(gstr3b.net_tax_payable)}</span></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Input Tax Register */}
      {inputTax && (
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader><CardTitle className="text-lg font-manrope">Input Tax Register</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {['CGST', 'SGST', 'IGST'].map(c => (
                <div key={c} className="text-center p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                  <p className="text-xl font-mono font-bold text-emerald-400">{formatCurrency(inputTax.totals?.[c] || 0)}</p>
                  <p className="text-sm text-muted-foreground">{c}</p>
                </div>
              ))}
              <div className="text-center p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-xl font-mono font-bold text-emerald-400">{formatCurrency(inputTax.total_input_tax || 0)}</p>
                <p className="text-sm text-muted-foreground">Total ITC</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">{inputTax.entries?.length || 0} entries from vendor bills</p>
          </CardContent>
        </Card>
      )}

      {/* Output Tax Register */}
      {outputTax && (
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader><CardTitle className="text-lg font-manrope">Output Tax Register</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {['CGST', 'SGST', 'IGST'].map(c => (
                <div key={c} className="text-center p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <p className="text-xl font-mono font-bold text-blue-400">{formatCurrency(outputTax.totals?.[c] || 0)}</p>
                  <p className="text-sm text-muted-foreground">{c}</p>
                </div>
              ))}
              <div className="text-center p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-xl font-mono font-bold text-blue-400">{formatCurrency(outputTax.total_output_tax || 0)}</p>
                <p className="text-sm text-muted-foreground">Total Output</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">{outputTax.entries?.length || 0} entries from POS sales</p>
          </CardContent>
        </Card>
      )}

      {/* TDS Summary */}
      {tdsSummary && (
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-manrope">TDS Summary</CardTitle>
            <Badge variant="outline" className={tdsSummary.status === 'LOCKED' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}>{tdsSummary.status || 'OPEN'}</Badge>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 rounded-lg bg-purple-500/5 border border-purple-500/20">
                <p className="text-2xl font-mono font-bold text-purple-400">{formatCurrency(tdsSummary.total_tds_deducted)}</p>
                <p className="text-sm text-muted-foreground">TDS Deducted</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <p className="text-2xl font-mono font-bold text-emerald-400">{formatCurrency(tdsSummary.tds_paid)}</p>
                <p className="text-sm text-muted-foreground">TDS Paid</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <p className="text-2xl font-mono font-bold text-amber-400">{formatCurrency(tdsSummary.tds_payable)}</p>
                <p className="text-sm text-muted-foreground">TDS Payable</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Tax History Tab ─────────────────────────────────────────

function TaxHistoryTab() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await accountsAPI.getTaxConfigHistory();
      setHistory(res.data.history || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load history');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={`hist-skel-${i}`} className="h-16 rounded-xl" />)}</div>;

  if (history.length === 0) {
    return (
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardContent className="py-8 text-center">
          <Clock className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
          <p className="text-sm text-zinc-400">No tax configuration history yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="tax-history-timeline">
      {history.map((config, idx) => (
        <Card key={config.config_id} className={`bg-zinc-900/60 border-zinc-800 ${config.is_active ? 'border-l-2 border-l-emerald-500' : 'opacity-70'}`}>
          <CardContent className="py-3 px-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={config.scope_type === 'OUTLET' ? sourceColors.OUTLET_TAX_CONFIG : sourceColors.ORG_TAX_CONFIG}>
                    {config.scope_type === 'OUTLET' ? 'Outlet' : 'Org Default'}
                  </Badge>
                  <span className="text-xs text-zinc-400 font-mono">v{config.version}</span>
                  {config.is_active && <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px] h-4">Active</Badge>}
                  {!config.is_active && <Badge variant="outline" className="text-zinc-500 border-zinc-700 text-[10px] h-4">Inactive</Badge>}
                </div>
                <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                  {ACCOUNTS.map(acct => {
                    const rule = config.rules?.[acct];
                    if (!rule) return null;
                    return (
                      <span key={acct} className="text-[10px] text-zinc-400">
                        <span className={acctColors[acct]?.dot ? '' : ''}>{acct}</span>: {rule.tax_type} {rule.total_rate}% {rule.tax_mode}
                      </span>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-500">
                  <span>Effective: {formatDate(config.effective_from)}</span>
                  {config.effective_to && <span>Until: {formatDate(config.effective_to)}</span>}
                  {config.created_by?.name && <span>By: {config.created_by.name}</span>}
                </div>
              </div>
              <span className="text-[10px] text-zinc-600 font-mono whitespace-nowrap">{config.config_id?.slice(0, 8)}</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Main Tax Dashboard Page ─────────────────────────────────

const SOURCE_DOT_CLASS = {
  OUTLET_TAX_CONFIG: 'bg-cyan-400',
  ORG_TAX_CONFIG: 'bg-teal-400',
};

const TaxDashboardPage = () => {
  const { selectedOrg } = useApp();
  const [activeTab, setActiveTab] = useState('definitions');
  const periodState = usePeriod(PERIOD_MODES.MONTH_SELECTION);
  const [resolvedPeriod, setResolvedPeriod] = useState(null);

  if (!selectedOrg) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an organization</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tax-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-manrope font-bold tracking-tight">Tax Dashboard</h1>
          <p className="text-muted-foreground mt-1">Define, view, and validate tax configuration</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {activeTab === 'applied' && (
            <>
              <PeriodToolbar state={periodState} mode={PERIOD_MODES.MONTH_SELECTION} resolved={resolvedPeriod} />
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-zinc-900/50 p-1 rounded-xl w-fit" data-testid="tax-dashboard-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                isActive
                  ? 'bg-zinc-800 text-zinc-100 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50'
              }`}
              data-testid={`tax-tab-${tab.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'definitions' && <TaxDefinitionsTab />}
      {activeTab === 'applied' && <TaxAppliedTab months={periodState.months} onResolved={setResolvedPeriod} onPickMonth={(m) => periodState.setMonths([m])} />}
      {activeTab === 'history' && <TaxHistoryTab />}
    </div>
  );
};

export default TaxDashboardPage;
